import type { MRT_VisibilityState } from "material-react-table";
import { useCallback, useContext, useEffect, useState } from "react";
import { TableColumnPreferencesContext } from "_shared/_views/tables/table-column-preferences-context";

/**
 * Which columns a reader has hidden in a table's Show/Hide columns menu (#2788).
 *
 * Held in component state alone, the choice went with the table: leave the page, reload or sign in
 * again and every hidden column came back. A reader who always works with a trimmed table had to trim
 * it on every visit.
 *
 * The choice is kept on the reader's account, through `TableColumnPreferencesProvider`, so it follows
 * them to another browser and survives signing out. This browser keeps a copy too, under the key
 * below, so a table opens trimmed before the server has answered and stays trimmed when it cannot.
 *
 * What is kept is the reader's choice and nothing else. A table that also forces columns off — an
 * Archived-only column, a column that only makes sense for some rows, a phone layout — merges that
 * over this state on every render and never hands the merged result back here, so a forced column is
 * never saved and a saved choice never overrides one.
 */
export function columnVisibilityStorageKey(tableKey: string): string {
  return `${columnVisibilityStorageKeyPrefix}${tableKey}${columnVisibilityStorageKeySuffix}`;
}

/** What every table's copy is filed under, so signing out can forget them all. */
export const columnVisibilityStorageKeyPrefix = "attorneyhub.table.";
const columnVisibilityStorageKeySuffix = ".columnVisibility.v1";

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

/**
 * A saved choice, made safe to hand to MRT.
 *
 * It takes `unknown` because what it is handed came out of storage a previous version of this code
 * wrote, and nothing guarantees it is still a map of column ids to booleans. Anything else is dropped
 * entry by entry rather than throwing the whole choice away. A column id the table no longer has is
 * kept and harmless — MRT looks up only the columns it has — and a column added after the choice was
 * saved has no entry, so it is shown.
 */
export function parseColumnVisibility(saved: unknown): MRT_VisibilityState {
  if (typeof saved !== "object" || saved === null || Array.isArray(saved)) return {};
  return Object.fromEntries(
    Object.entries(saved).filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"),
  );
}

export function readColumnVisibility(
  tableKey: string,
  storage: ReadableStorage | undefined = getBrowserLocalStorage(),
): MRT_VisibilityState {
  let saved: string | null | undefined;
  try {
    saved = storage?.getItem(columnVisibilityStorageKey(tableKey));
  } catch (error) {
    console.warn(`Could not read the saved columns for the ${tableKey} table.`, error);
    return {};
  }
  if (!saved) return {};

  try {
    return parseColumnVisibility(JSON.parse(saved));
  } catch {
    // Not JSON at all. The table opens with every column it would show anyway, and the reader's next
    // choice overwrites what was there.
    return {};
  }
}

export function writeColumnVisibility(
  tableKey: string,
  visibility: MRT_VisibilityState,
  storage: WritableStorage | undefined = getBrowserLocalStorage(),
): void {
  try {
    storage?.setItem(columnVisibilityStorageKey(tableKey), JSON.stringify(visibility));
  } catch (error) {
    // Storage the browser refuses — a private window with it disabled, a quota already full. The
    // column is still hidden for this visit; it simply does not stay hidden. Warn rather than rethrow:
    // there is nothing the reader can do about it, but this is the only trace of why their choice did
    // not survive a reload.
    console.warn(`Could not remember the columns chosen for the ${tableKey} table.`, error);
  }
}

type ColumnVisibilityUpdater =
  | MRT_VisibilityState
  | ((previous: MRT_VisibilityState) => MRT_VisibilityState);

type HeldColumnVisibility = {
  visibility: MRT_VisibilityState;
  /** False until the reader changes something, so opening a table never writes a choice of its own. */
  chosen: boolean;
  /** The table's view revision when the reader chose: a view opened since then outranks the choice. */
  revision: number;
};

/** The choice of a table the server knows nothing about: every column as the table defines it. */
const NO_CHOICE: MRT_VisibilityState = Object.freeze({});

/**
 * The reader's Show/Hide columns choice for one table, read when the table mounts and saved whenever
 * the reader changes it.
 *
 * Handed back the way `useState` hands back its pair, so a table that kept the choice in `useState`
 * swaps one call for the other and passes the setter to MRT's `onColumnVisibilityChange` unchanged.
 * Without a `tableKey` nothing is read or saved and it is exactly that `useState`, which is what lets
 * `MRTNoMemo` call it for every table and persist only the ones that opt in.
 *
 * Which choice the table opens with:
 * - until the server answers, this browser's copy;
 * - once it has, the server's — and a table it has no choice for opens with its defaults, even if this
 *   browser remembers otherwise, because the account is the record and the copy may be another
 *   reader's or older than a choice made elsewhere;
 * - once the reader changes something here, their change, whatever arrives after it;
 * - once the reader opens a saved view from the Views menu (#6717), that view's, until they change
 *   something again.
 *
 * The key is read on mount only. A table whose key can change while it stays mounted should be keyed
 * on it, so that it remounts and reads the other table's choice.
 */
export function usePersistedColumnVisibility(
  tableKey: string | undefined,
): [MRT_VisibilityState, (updater: ColumnVisibilityUpdater) => void] {
  const preferences = useContext(TableColumnPreferencesContext);
  const savedTables = tableKey ? preferences?.savedTables : undefined;
  const serverChoice = tableKey && savedTables ? (savedTables.get(tableKey) ?? NO_CHOICE) : undefined;
  const saveTable = preferences?.saveTable;
  const refresh = preferences?.refresh;
  const revision = tableKey ? (preferences?.viewRevisions?.get(tableKey) ?? 0) : 0;

  const [held, setHeld] = useState<HeldColumnVisibility>(() => ({
    visibility: tableKey ? readColumnVisibility(tableKey) : {},
    chosen: false,
    revision,
  }));
  const chosenHere = held.chosen && held.revision === revision;
  const visibility = chosenHere || serverChoice === undefined ? held.visibility : serverChoice;

  // A table that opens asks for the account's choices again, so it shows a reset or a choice made in
  // another browser since this tab last asked. Once, on mount: the answer reaches it through context.
  const [refreshOnMount] = useState(() => refresh);
  useEffect(() => {
    if (tableKey) refreshOnMount?.();
  }, [refreshOnMount, tableKey]);

  useEffect(() => {
    if (!tableKey || !held.chosen) return;
    writeColumnVisibility(tableKey, held.visibility);
    saveTable?.(tableKey, held.visibility);
  }, [held, tableKey, saveTable]);

  // Keeps this browser's copy level with the account, so the next visit opens with the right columns
  // before the server has answered.
  useEffect(() => {
    if (!tableKey || chosenHere || serverChoice === undefined) return;
    if (serverChoice === NO_CHOICE) removeColumnVisibility(tableKey);
    else writeColumnVisibility(tableKey, serverChoice);
  }, [chosenHere, serverChoice, tableKey]);

  // Stable while the server's answer is, as `useState`'s own setter is: tables list it in the
  // dependencies of the option object they build for MRT, and a new function every render would
  // rebuild that every render. The updater is applied to what the table is showing, which is the
  // server's choice until the reader has made one here.
  const setVisibility = useCallback(
    (updater: ColumnVisibilityUpdater) => {
      setHeld((previous) => {
        const choseHere = previous.chosen && previous.revision === revision;
        const current = choseHere || serverChoice === undefined ? previous.visibility : serverChoice;
        return { visibility: typeof updater === "function" ? updater(current) : updater, chosen: true, revision };
      });
    },
    [revision, serverChoice],
  );

  return [visibility, setVisibility];
}

/** Forgets this browser's copy of one table's choice. */
export function removeColumnVisibility(
  tableKey: string,
  storage: Pick<Storage, "removeItem"> | undefined = getBrowserLocalStorage(),
): void {
  try {
    storage?.removeItem(columnVisibilityStorageKey(tableKey));
  } catch (error) {
    console.warn(`Could not forget the columns chosen for the ${tableKey} table.`, error);
  }
}

/** Whether this browser keeps a copy of any table's choice, known to the account or not. */
export function hasAnyColumnVisibility(
  storage: Pick<Storage, "key" | "length"> | undefined = getBrowserLocalStorage(),
): boolean {
  try {
    for (let index = 0; index < (storage?.length ?? 0); index += 1) {
      if (isColumnVisibilityStorageKey(storage?.key(index))) return true;
    }
  } catch (error) {
    console.warn("Could not read the columns chosen for the tables.", error);
  }
  return false;
}

function isColumnVisibilityStorageKey(key: string | null | undefined): key is string {
  return Boolean(key?.startsWith(columnVisibilityStorageKeyPrefix) && key.endsWith(columnVisibilityStorageKeySuffix));
}

/**
 * Forgets this browser's copy of every table's choice, for Settings' "Reset all tables" (#6151).
 *
 * It removes the copies of tables the account has no choice for as well: a copy the server never
 * received — a save that failed — would otherwise open that table trimmed until the server answered.
 */
export function removeAllColumnVisibility(
  storage: Pick<Storage, "key" | "length" | "removeItem"> | undefined = getBrowserLocalStorage(),
): void {
  try {
    // Backwards, because each removal moves the keys after it down one place.
    for (let index = (storage?.length ?? 0) - 1; index >= 0; index -= 1) {
      const key = storage?.key(index);
      if (isColumnVisibilityStorageKey(key)) {
        storage?.removeItem(key);
      }
    }
  } catch (error) {
    console.warn("Could not forget the columns chosen for every table.", error);
  }
}

function getBrowserLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
